/** Angeheftete Nachrichten eines Kanals (Liste, Springen, Lösen); beim Öffnen gelesen markieren (ack-pins). */
import type { MessageDto } from '@lagerfeuer/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PinOff } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Avatar } from '../../components/Avatar';
import { Popover, type Anchor } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { errorMessage } from '../../lib/api';
import { channelsApi, messagesApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { pathForChannel } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { setPinned } from './actions';
import { formatMessageTime } from './format';
import { Markdown } from './Markdown';
import { useAuthor } from './MessageItem';
import { useMessagePerms } from './perms';
import './messages.css';

function PinItem({ m, canUnpin, onJump, onUnpin }: { m: MessageDto; canUnpin: boolean; onJump: () => void; onUnpin: () => void }) {
  const author = useAuthor(m, m.serverId);
  return (
    <li className="pin-item" data-testid="pin-item" data-message-id={m.id}>
      <div className="pin-head">
        <Avatar user={author.user} overrideUrl={author.avatarUrl} size={24} />
        <strong style={author.color ? { color: author.color } : undefined} className="truncate">
          {author.name}
        </strong>
        <time className="faint small" dateTime={m.createdAt}>
          {formatMessageTime(m.createdAt)}
        </time>
        <span className="spacer" />
        <button type="button" className="btn btn-sm" onClick={onJump} data-testid="pin-jump">
          Springen
        </button>
        {canUnpin && (
          <Tooltip label="Lösen">
            <button type="button" className="icon-btn" onClick={onUnpin} data-testid="pin-unpin">
              <PinOff size={16} />
            </button>
          </Tooltip>
        )}
      </div>
      {m.content && <Markdown content={m.content} serverId={m.serverId} className="pin-content" />}
      {m.attachments.length > 0 && <div className="faint small">{m.attachments.length === 1 ? '1 Anhang' : `${m.attachments.length} Anhänge`}</div>}
    </li>
  );
}

export function PinsPopover({ channelId, anchor, onClose }: { channelId: string; anchor: Anchor; onClose: () => void }) {
  const channel = useAppStore((s) => s.channels[channelId]);
  const perms = useMessagePerms(channelId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.pins(channelId), queryFn: () => messagesApi.pins(channelId) });

  useEffect(() => {
    channelsApi
      .ackPins(channelId)
      .then((rs) => rs && useAppStore.getState().dispatch('READ_STATE_UPDATE', rs))
      .catch(() => undefined);
  }, [channelId]);

  return (
    <Popover anchor={anchor} onClose={onClose} placement="bottom-end" testId="pins-popover" label="Angeheftete Nachrichten" className="pins-popover">
      <div className="pins-header">
        <h3>Angeheftete Nachrichten</h3>
      </div>
      <div className="pins-body scroll">
        {q.isLoading && (
          <div className="empty">
            <div className="spinner" />
          </div>
        )}
        {q.isError && <div className="empty error-text">Konnte nicht geladen werden: {errorMessage(q.error)}</div>}
        {q.data && q.data.length === 0 && (
          <div className="empty" data-testid="pins-empty">
            <p>In diesem Kanal ist noch nichts angeheftet.</p>
          </div>
        )}
        {q.data && q.data.length > 0 && (
          <ul className="pins-list">
            {q.data.map((m) => (
              <PinItem
                key={m.id}
                m={m}
                canUnpin={perms.canPin}
                onJump={() => {
                  onClose();
                  if (channel) void navigate(pathForChannel(channel, m.id));
                }}
                onUnpin={() =>
                  void setPinned(m, false).then((ok) => {
                    if (ok) qc.setQueryData<MessageDto[]>(qk.pins(channelId), (old) => old?.filter((x) => x.id !== m.id));
                  })
                }
              />
            ))}
          </ul>
        )}
      </div>
    </Popover>
  );
}
